import { ChangeDetectionStrategy, Component, inject, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BtnApiService, BtnImportRow, BtnImportState } from '../../../../shared/services/api/btn-api.service';
import { XIconButtonComponent } from '../../../../shared/x-kit/components/x-icon-button/x-icon-button.component';
import { XLabelComponent } from '../../../../shared/x-kit/components/x-label/x-label.component';
import { XCommonRowComponent } from '../../../../shared/x-kit/components/x-common-row/x-common-row.component';
import { XButtonComponent } from '../../../../shared/x-kit/components/x-button/x-button.component';
import { TranslatePipe } from '@ngx-translate/core';

export interface BtnUploadInfoDialogData {
  id: string;
  name: string;
  handled: number;
  total: number;
  onOpenTable: (id: string) => void;
  // Added for detailed stats
  tableRows?: BtnImportRow[];
  status?: string;
  // Simple mode: show only success message and button
  simpleMode?: boolean;
}

@Component({
  selector: 'app-btn-upload-info-dialog',
  standalone: true,
  imports: [CommonModule, XIconButtonComponent, XLabelComponent, XCommonRowComponent, XButtonComponent, TranslatePipe],
  template: `
    <div class="dialog-container">
      <!-- Simple mode: just success message and button -->
      <ng-container *ngIf="data.simpleMode">
        <div class="success-icon">✅</div>
        <div class="success-title">{{ 'btn_notification_file_processed_title' | translate }}</div>
        <div class="success-message">{{ 'btn_notification_file_processed_message' | translate: { fileName: data.name } }}</div>
        
        <div class="actions">
          <app-x-button [style]="'outlined'" (click)="close()">{{ 'btn_notification_close' | translate }}</app-x-button>
          <app-x-button [style]="'filled'" (click)="openTable()">{{ 'btn_notification_open_table' | translate }}</app-x-button>
        </div>
      </ng-container>

      <!-- Full mode: detailed stats -->
      <ng-container *ngIf="!data.simpleMode">
        <div class="title">{{ data.name }}</div>

        <div class="divider"></div>

        <div class="info-row">
          <span class="info-text">{{ 'btn_upload_info_total_rows' | translate }}</span>
          <span class="info-text">{{ data.total }}</span>
        </div>
        <div class="info-row">
          <span class="info-text">{{ 'btn_upload_info_handled' | translate }}</span>
          <span class="info-text">{{ data.handled }} / {{ data.total }}</span>
        </div>

        <!-- Show stats breakdown only when processing is complete -->
        <ng-container *ngIf="isDone && data.tableRows">
          <div class="divider stats-divider"></div>
          <div class="info-row">
            <span class="info-text">{{ 'btn_upload_info_matched' | translate }}</span>
            <span class="info-text">{{ stats.matched }}</span>
          </div>
          <div class="info-row">
            <span class="info-text">{{ 'btn_upload_info_not_found' | translate }}</span>
            <span class="info-text">{{ stats.not_found }}</span>
          </div>
        </ng-container>

        <div class="actions">
          <app-x-button [style]="'outlined'" (click)="close()">{{ 'btn_upload_info_close' | translate }}</app-x-button>
          <app-x-button [style]="'filled'" (click)="openTable()">{{ 'btn_upload_info_open_table' | translate }}</app-x-button>
        </div>
      </ng-container>
    </div>
  `,
  styleUrl: './btn-upload-info-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BtnUploadInfoDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<BtnUploadInfoDialogComponent>);
  private btnApi = inject(BtnApiService);

  public stats = { matched: 0, not_found: 0, review: 0 };
  public isDone = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: BtnUploadInfoDialogData) {}

  ngOnInit(): void {
    if (this.data.tableRows && this.data.tableRows.length > 0) {
      this.stats.matched = this.data.tableRows.filter((r: BtnImportRow) => r.result === 'matched').length;
      this.stats.not_found = this.data.tableRows.filter(
        (r: BtnImportRow) => r.result === 'not_found' || r.result === 'not_found_address',
      ).length;
      this.stats.review = this.data.tableRows.filter((r: BtnImportRow) => r.result === 'review').length;
    }
    this.isDone = this.data.status === 'done' || this.data.status === 'handled';
  }

  openTable() {
    this.data.onOpenTable(this.data.id);
    this.close();
  }

  close() {
    this.dialogRef.close();
  }
}


